import type { ProfileData } from "../api/getProfile";
import type { TransactionData } from "../api/transaction";
import { dedupeInFlight } from "./inFlight";

export const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === "true";

const MOCK_STORE_KEY = "mock_store";

type MockTransactionOverride = {
  amount?: number;
  remarks?: string;
  payment_method?: string;
  status?: TransactionData["status"];
};

type MockStore = {
  profile?: Partial<ProfileData>;
  transactions?: Record<string, MockTransactionOverride>;
};

export const mockDelay = (ms = 300) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const loadMock = <T>(file: string): Promise<T> =>
  dedupeInFlight(`GET /mock/${file}`, async () => {
    const response = await fetch(`/mock/${file}`);
    if (!response.ok) {
      throw new Error(`Failed to load mock ${file}`);
    }
    await mockDelay();
    return response.json();
  });

const readMockStore = (): MockStore => {
  try {
    return JSON.parse(localStorage.getItem(MOCK_STORE_KEY) || "{}");
  } catch {
    return {};
  }
};

const writeMockStore = (store: MockStore) =>
  localStorage.setItem(MOCK_STORE_KEY, JSON.stringify(store));

export const getMockProfile = (): Partial<ProfileData> =>
  readMockStore().profile || {};

export const setMockProfile = (patch: Partial<ProfileData>) => {
  const store = readMockStore();
  writeMockStore({ ...store, profile: { ...store.profile, ...patch } });
};

export const getMockTransaction = (
  transactionId: string
): MockTransactionOverride => readMockStore().transactions?.[transactionId] || {};

export const setMockTransaction = (
  transactionId: string,
  patch: MockTransactionOverride
) => {
  const store = readMockStore();
  writeMockStore({
    ...store,
    transactions: {
      ...store.transactions,
      [transactionId]: { ...store.transactions?.[transactionId], ...patch },
    },
  });
};
